import type { MetricWarning } from '../contracts';
import { partialMerge } from '../warnings';

export interface MergeResult {
  /** Sum of the known incoming flows; null only when there are zero known flows and at least one incoming edge. */
  value: number | null;
  /** True when at least one incoming flow is unknown, so `value` is a partial sum, not a total. */
  partial: boolean;
  warnings: MetricWarning[];
}

/**
 * SPEC-008 §47/§48/METRIC-REQ-017/plan §20 — merges known incoming edge
 * flows. A partial sum is never presented as a complete total without a
 * warning.
 */
export function mergeIncomingFlows(flows: readonly (number | null)[], nodeId?: string): MergeResult {
  if (flows.length === 0) {
    return { value: null, partial: false, warnings: [] };
  }
  const known = flows.filter((flow): flow is number => flow !== null);
  const hasUnknown = known.length < flows.length;
  if (known.length === 0) {
    return { value: null, partial: hasUnknown, warnings: [] };
  }
  const sum = known.reduce((total, flow) => total + flow, 0);
  if (hasUnknown) {
    return { value: sum, partial: true, warnings: [partialMerge(undefined, nodeId ? [nodeId] : undefined)] };
  }
  return { value: sum, partial: false, warnings: [] };
}
